/**
 * PatientADLFlagsPanel.jsx
 * CCH Care — Per-shift N/A (2026-05-29)
 *
 * Supervisor/Admin panel for managing which ADL sections apply
 * to a specific resident, on which shifts.
 *
 * Per-shift N/A (2026-05-29):
 * - Each canBeNA section now shows a per-shift control (AM/PM/NOC)
 *   instead of a single section-wide toggle. The supervisor can mark
 *   any combination of shifts N/A while leaving others applicable.
 * - vitals.canBeNA flipped to true. Typical LTC convention is vitals
 *   on AM only; supervisors will commonly mark PM and NOC as N/A.
 * - Legacy "all shifts N/A" (shift_type='ALL' from before this change)
 *   is detected and surfaced as a separate UI state with a Decompose
 *   button. Decomposing replaces the ALL flag with three shift-specific
 *   flags so per-shift toggles work cleanly afterward.
 *
 * Props:
 *   patientId    — int
 *   patientName  — string
 *   onClose      — callback (optional, if rendered in a modal)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { COLORS } from '../../config/constants';
import { getPatientADLFlags, setPatientADLFlag, resetPatientADLFlag } from '../../api/adl';

const SHIFT_TYPES = ['AM', 'PM', 'NOC'];

const SECTIONS = [
  { key: 'hygiene',       label: 'Hygiene',      icon: '🛁', canBeNA: false,
    note: 'Hygiene applies to all residents on every shift.' },
  { key: 'meals',         label: 'Meals',         icon: '🍽️', canBeNA: false,
    note: 'Meals applies to all residents on every shift.' },
  { key: 'repositioning', label: 'Repositioning', icon: '🔄', canBeNA: true,
    note: 'May be N/A for ambulatory residents who reposition independently.' },
  { key: 'fluid_intake',  label: 'Fluid Intake',  icon: '💧', canBeNA: false,
    note: 'Fluid intake tracking applies to all residents on every shift.' },
  { key: 'bowel',         label: 'Bowel',         icon: '📋', canBeNA: false,
    note: 'Bowel monitoring applies to all residents on every shift.' },
  { key: 'urination',     label: 'Urination',     icon: '🚽', canBeNA: false,
    note: 'Urination monitoring applies to all residents on every shift.' },
  { key: 'skin_checks',   label: 'Skin Checks',   icon: '👁️', canBeNA: true,
    note: 'May be N/A for residents without skin integrity concerns per physician order.' },
  { key: 'safety_checks', label: 'Safety Checks', icon: '✅', canBeNA: false,
    note: 'Safety checks apply to all residents on every shift.' },
  { key: 'vitals',        label: 'Vitals',        icon: '🩺', canBeNA: true,
    note: 'Mark N/A on shifts where vitals are not ordered (typically PM and NOC).' },
];

export default function PatientADLFlagsPanel({ patientId, patientName, onClose }) {
  // flagsByShift shape: { section: { 'ALL'|'AM'|'PM'|'NOC': flagObj } }
  // — provided by the get endpoint as `flags_by_shift`. We treat the
  // 'ALL' key specially in the UI (decompose path); per-shift keys are
  // the operational ones once decomposed.
  const [flagsByShift, setFlagsByShift] = useState({});
  const [loading, setLoading]           = useState(true);
  const [saving,  setSaving]            = useState(null); // { section, shift }
  const [error,   setError]             = useState('');

  // Reason editor — keyed by (section, shift) tuple while open.
  const [editTarget, setEditTarget] = useState(null); // { section, shift }
  const [editReason, setEditReason] = useState('');
  const reasonInputRef = useRef(null);

  const refresh = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    getPatientADLFlags(patientId)
      .then(d => { if (d.success) setFlagsByShift(d.flags_by_shift || {}); })
      .catch(() => setError('Failed to load flags'))
      .finally(() => setLoading(false));
  }, [patientId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Focus reason input when editing opens
  useEffect(() => {
    if (editTarget && reasonInputRef.current) {
      reasonInputRef.current.focus();
    }
  }, [editTarget]);

  function isSaving(section, shift) {
    return saving && saving.section === section && saving.shift === shift;
  }
  function isAnySaving() { return saving !== null; }

  // Get the effective state of a (section, shift) pill.
  // Returns { isNA, reason, source } where source is 'specific' | 'all' | null.
  function pillState(section, shift) {
    const sectionFlags = flagsByShift[section] || {};
    const specific = sectionFlags[shift];
    if (specific && specific.is_applicable === false) {
      return { isNA: true, reason: specific.reason ?? '', source: 'specific' };
    }
    if (specific && specific.is_applicable === true) {
      return { isNA: false, reason: '', source: 'specific' };
    }
    const allFlag = sectionFlags['ALL'];
    if (allFlag && allFlag.is_applicable === false) {
      return { isNA: true, reason: allFlag.reason ?? '', source: 'all' };
    }
    return { isNA: false, reason: '', source: null };
  }

  // Section is in "legacy ALL-N/A" state when there's an ALL flag with
  // is_applicable=false and NO specific overrides. The supervisor sees
  // a single "All shifts N/A" banner with a Decompose button.
  function legacyAllState(section) {
    const sectionFlags = flagsByShift[section] || {};
    const allFlag = sectionFlags['ALL'];
    if (!allFlag || allFlag.is_applicable !== false) return null;
    const hasSpecific = SHIFT_TYPES.some(s => sectionFlags[s]);
    if (hasSpecific) return null;
    return { reason: allFlag.reason ?? '' };
  }

  // Toggle a single shift to N/A — opens the reason editor.
  function openSetNA(section, shift) {
    setEditTarget({ section, shift });
    setEditReason('');
    setError('');
  }

  // Toggle a single shift back to applicable — deletes the specific flag.
  async function handleRestore(section, shift) {
    setSaving({ section, shift });
    setError('');
    try {
      await resetPatientADLFlag(patientId, section, shift);
      setFlagsByShift(prev => {
        const next = { ...prev };
        const sec  = { ...(next[section] || {}) };
        delete sec[shift];
        next[section] = sec;
        return next;
      });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to restore');
    } finally {
      setSaving(null);
    }
  }

  // Submit the reason editor — writes a specific (section, shift, N/A) flag.
  async function handleConfirmNA() {
    if (!editTarget) return;
    const { section, shift } = editTarget;
    setSaving({ section, shift });
    setError('');
    try {
      const data = await setPatientADLFlag(
        patientId, section, false, editReason, null, shift,
      );
      if (data.success) {
        setFlagsByShift(prev => {
          const next = { ...prev };
          const sec  = { ...(next[section] || {}) };
          sec[shift] = { is_applicable: false, reason: editReason, shift_type: shift };
          next[section] = sec;
          return next;
        });
        setEditTarget(null);
        setEditReason('');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to update flag');
    } finally {
      setSaving(null);
    }
  }

  // Decompose a legacy ALL-N/A flag into three specific N/A flags so the
  // per-shift toggles work cleanly. Preserves the original reason on all
  // three. The supervisor can then turn individual shifts back on.
  async function handleDecompose(section) {
    const legacy = legacyAllState(section);
    if (!legacy) return;
    setSaving({ section, shift: '*' });
    setError('');
    try {
      for (const shift of SHIFT_TYPES) {
        await setPatientADLFlag(patientId, section, false, legacy.reason, null, shift);
      }
      await resetPatientADLFlag(patientId, section, null); // null → 'ALL'
      refresh();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Decompose failed');
    } finally {
      setSaving(null);
    }
  }

  // Restore an entire section to applicable on all shifts.
  async function handleRestoreAll(section) {
    const sectionFlags = flagsByShift[section] || {};
    const targets = [
      ...(sectionFlags['ALL'] ? ['ALL'] : []),
      ...SHIFT_TYPES.filter(s => sectionFlags[s]),
    ];
    if (targets.length === 0) return;
    setSaving({ section, shift: '*' });
    setError('');
    try {
      for (const shift of targets) {
        await resetPatientADLFlag(
          patientId, section, shift === 'ALL' ? null : shift,
        );
      }
      refresh();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Restore failed');
    } finally {
      setSaving(null);
    }
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>
            ADL Section Applicability
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
            {patientName} · Set which sections apply on which shifts
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: COLORS.textMuted }}
            aria-label="Close"
          >
            ✕
          </button>
        )}
      </div>

      {error && (
        <div style={{
          background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`,
          borderRadius: 8, padding: '8px 12px', marginBottom: 12,
          fontSize: 12, color: COLORS.danger,
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 24, color: COLORS.textMuted }}>Loading&hellip;</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {SECTIONS.map(sec => {
            const legacy   = legacyAllState(sec.key);
            const anyNA    = SHIFT_TYPES.some(s => pillState(sec.key, s).isNA);
            const sectionSaving = saving?.section === sec.key;

            return (
              <div key={sec.key} style={{
                background: anyNA ? COLORS.bg : COLORS.surface,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 10, padding: '12px 14px',
                opacity: sectionSaving ? 0.7 : 1, transition: 'opacity 0.15s',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ fontSize: 18, flexShrink: 0, marginTop: 2 }}>{sec.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{sec.label}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>{sec.note}</div>
                  </div>

                  {!sec.canBeNA && (
                    <span style={{
                      fontSize: 10, color: COLORS.textMuted,
                      background: COLORS.bg, padding: '2px 8px',
                      borderRadius: 10, border: `1px solid ${COLORS.border}`,
                      flexShrink: 0, marginTop: 2,
                    }}>
                      Always required
                    </span>
                  )}
                </div>

                {/* Legacy all-shifts-N/A state with Decompose CTA */}
                {sec.canBeNA && legacy && (
                  <div style={{
                    marginTop: 10, padding: '10px 12px',
                    background: COLORS.warningLight ?? COLORS.bg,
                    border: `1px solid ${COLORS.warning ?? COLORS.border}`,
                    borderRadius: 8,
                  }}>
                    <div style={{ fontSize: 12, color: COLORS.text, marginBottom: 6 }}>
                      <strong>All shifts marked N/A.</strong>
                      {legacy.reason && (
                        <span style={{ color: COLORS.textMuted, fontStyle: 'italic' }}>
                          {' '}— {legacy.reason}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleDecompose(sec.key)}
                        disabled={isAnySaving()}
                        style={{
                          fontSize: 11, padding: '5px 12px', borderRadius: 6,
                          background: COLORS.primary, color: '#fff', border: 'none',
                          fontWeight: 600,
                          cursor: isAnySaving() ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {sectionSaving ? 'Working…' : 'Customize per shift'}
                      </button>
                      <button
                        onClick={() => handleRestoreAll(sec.key)}
                        disabled={isAnySaving()}
                        style={{
                          fontSize: 11, padding: '5px 12px', borderRadius: 6,
                          background: 'none',
                          color: COLORS.primary,
                          border: `1px solid ${COLORS.primary}`,
                          cursor: isAnySaving() ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Restore all
                      </button>
                    </div>
                  </div>
                )}

                {/* Per-shift toggles (canBeNA sections without a legacy ALL flag) */}
                {sec.canBeNA && !legacy && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {SHIFT_TYPES.map(shift => {
                        const state    = pillState(sec.key, shift);
                        const thisSaving = isSaving(sec.key, shift);
                        const isEditingThis = editTarget?.section === sec.key && editTarget?.shift === shift;

                        return (
                          <ShiftPill
                            key={shift}
                            shift={shift}
                            isNA={state.isNA}
                            reason={state.reason}
                            saving={thisSaving}
                            disabled={isAnySaving() && !thisSaving}
                            editing={isEditingThis}
                            onSetNA={() => openSetNA(sec.key, shift)}
                            onRestore={() => handleRestore(sec.key, shift)}
                          />
                        );
                      })}
                    </div>

                    {anyNA && (
                      <div style={{ marginTop: 8 }}>
                        <button
                          onClick={() => handleRestoreAll(sec.key)}
                          disabled={isAnySaving()}
                          style={{
                            fontSize: 11, padding: '4px 10px', borderRadius: 6,
                            background: 'none',
                            color: COLORS.textMuted,
                            border: `1px solid ${COLORS.border}`,
                            cursor: isAnySaving() ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Restore all shifts to applicable
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Inline reason form for the currently-editing pill */}
                {editTarget?.section === sec.key && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 6 }}>
                      Reason for marking <strong>{editTarget.shift}</strong> shift N/A
                      (required for the audit trail):
                    </div>
                    <input
                      ref={reasonInputRef}
                      type="text"
                      value={editReason}
                      onChange={e => setEditReason(e.target.value)}
                      placeholder="e.g. Vitals not ordered for PM/NOC per physician order dated…"
                      style={{
                        width: '100%', padding: '7px 10px', borderRadius: 6,
                        border: `1px solid ${COLORS.border}`, fontSize: 12,
                        fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 8,
                        background: COLORS.surface, color: COLORS.text,
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => { setEditTarget(null); setEditReason(''); }}
                        style={{
                          flex: 1, padding: '7px 0', borderRadius: 6,
                          background: 'none', border: `1px solid ${COLORS.border}`,
                          color: COLORS.textMuted, fontSize: 12, cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmNA}
                        disabled={!editReason.trim() || isAnySaving()}
                        style={{
                          flex: 2, padding: '7px 0', borderRadius: 6,
                          background: editReason.trim() ? COLORS.primary : COLORS.border,
                          color: editReason.trim() ? '#fff' : COLORS.textMuted,
                          border: 'none', fontSize: 12, fontWeight: 600,
                          cursor: !editReason.trim() || isAnySaving() ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {sectionSaving ? 'Saving…' : `Confirm ${editTarget.shift} N/A`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 16, lineHeight: 1.5 }}>
        Shifts marked N/A appear in the ADL flow sheet with an N/A badge and count
        toward completion for that shift only. Other shifts remain required. Changes
        take effect on the next shift.
      </p>
    </div>
  );
}

/**
 * ShiftPill
 * Single AM/PM/NOC toggle. Shows applicable (filled) or N/A (outlined).
 * Tapping flips state — applicable → opens reason editor,
 * N/A → restores immediately.
 */
function ShiftPill({ shift, isNA, reason, saving, disabled, editing, onSetNA, onRestore }) {
  const accent = COLORS.primary;
  const onClick = isNA ? onRestore : onSetNA;
  const label = saving ? 'Saving…' : shift;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={saving || disabled}
      title={isNA && reason ? `N/A — ${reason}` : undefined}
      style={{
        padding: '6px 12px',
        borderRadius: 20,
        fontSize: 12, fontWeight: 700,
        background: isNA ? COLORS.surface : accent,
        color:      isNA ? COLORS.textMuted : '#fff',
        border: `1px solid ${isNA ? COLORS.border : accent}`,
        cursor: saving || disabled ? 'not-allowed' : 'pointer',
        opacity: editing ? 0.85 : 1,
        display: 'inline-flex', alignItems: 'center', gap: 6,
        transition: 'all 0.15s',
      }}
    >
      <span>{label}</span>
      {!saving && (
        <span style={{ fontSize: 10, opacity: 0.85 }}>
          {isNA ? 'N/A' : '✓'}
        </span>
      )}
    </button>
  );
}